import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Dropdown, Menu, message } from "antd";
import { Link } from "react-router-dom";
import { FilterOutlined, DownOutlined } from "@ant-design/icons";
import { Bar, Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import moment from "moment";
import axios from "axios";
import Swal from "sweetalert2";
import DatePicker from "react-bootstrap-daterangepicker";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-daterangepicker/daterangepicker.css";
import exportService from "../../utils/exportService";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend } from "chart.js";
import PrintReportPreview from "../../reports/PrintReportPreview";
import { renderDate } from "../../utils/dateFormatter";
import SelectPartner from "../../components/ui/SelectPartner";
import PartnerService from "../../utils/partnerService";

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend);

const apiURL = process.env.REACT_APP_API_URL || "https://api.example.com";

// Status mapping to align with reservation report requirements
const statusMap = {
  checked_in: "confirmed",
  reserved: "tentative",
  checked_out: "checked_out",
  cancelled: "cancelled",
  no_show: "no_show",
};

const ReservationReport = () => {
  const { t } = useTranslation();
  const { getPartnerName } = PartnerService();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(() => {
    const business_date = localStorage.getItem("business_date");
    const start = business_date ? moment(business_date).startOf("month") : moment().startOf("month");
    return {
      startDate: start,
      endDate: moment(business_date || undefined).endOf("month"),
    };
  });
  const [statusFilter, setStatusFilter] = useState(null);
  const [partnerFilter, setPartnerFilter] = useState(null);
  const [purposeFilter, setPurposeFilter] = useState(null);
  const [petFilter, setPetFilter] = useState(null);
  const [guestTypeFilter, setGuestTypeFilter] = useState(null);
  const [showGraph, setShowGraph] = useState(true);
  const [pdfVisible, setPdfVisible] = useState(false);
  const [pdfData, setPdfData] = useState(null);

  useEffect(() => {
    fetchReport();
  }, [dateRange]);

  const fetchReport = async () => {
    setLoading(true);
    const token = localStorage.getItem("token");

    if (!dateRange.startDate || !dateRange.endDate || !moment(dateRange.startDate).isValid() || !moment(dateRange.endDate).isValid()) {
      Swal.fire({
        title: t("error"),
        text: t("invalid_date_range"),
        icon: "error",
      });
      setData([]);
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${apiURL}/reports-daily/ota-booking-volume`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          type: "reservation",
          startDate: dateRange.startDate.format("YYYY-MM-DD"),
          endDate: dateRange.endDate.format("YYYY-MM-DD"),
        },
      });
      if (response.data.success) {
        setData(response.data.otaBookings || []);
      } else {
        Swal.fire({
          title: t("error"),
          text: t("failed_fetch_reservation_report"),
          icon: "error",
        });
        setData([]);
      }
    } catch (error) {
      Swal.fire({
        title: t("error"),
        text: t("failed_fetch_reservation_report"),
        icon: "error",
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const getSourceName = (reservation) =>
    getPartnerName(reservation.booking_source_id) || reservation.business_source_name || "Unknown";

  const isDirectSource = (sourceName) => {
    const normalized = String(sourceName || "").trim().toLowerCase();
    return (
      normalized === "direct" ||
      normalized.includes("website") ||
      normalized.includes("repeat (direct)") ||
      normalized.includes("repeat direct") ||
      normalized.includes("whatsapp") ||
      normalized.includes("walk-in") ||
      normalized.includes("walk in") ||
      normalized.includes("instagram") ||
      normalized.includes("google / website")
    );
  };

  const getPurpose = (reservation) =>
    reservation.visit_purpose || reservation.purpose_of_visit || reservation.purpose || "";

  const getPetValue = (reservation) => {
    const value = reservation.travelling_with_pet ?? reservation.traveling_with_pet ?? reservation.pet;
    if (value === true || value === 1 || value === "true" || value === "yes" || value === "YES") return true;
    if (value === false || value === 0 || value === "false" || value === "no" || value === "NO") return false;
    return null;
  };

  const getPreviousStays = (reservation) =>
    Number(reservation.previous_stays ?? reservation.previous_stay_count ?? 0) || 0;

  const capabilities = useMemo(() => ({
    purpose: data.some((r) => Boolean(getPurpose(r))),
    pet: data.some((r) => getPetValue(r) !== null),
    repeat: data.some((r) =>
      Object.prototype.hasOwnProperty.call(r, "previous_stays") ||
      Object.prototype.hasOwnProperty.call(r, "previous_stay_count") ||
      Object.prototype.hasOwnProperty.call(r, "is_repeat_guest")
    ),
  }), [data]);

  const purposeOptions = useMemo(() =>
    [...new Set(data.map(getPurpose).filter(Boolean))]
      .sort((a, b) => String(a).localeCompare(String(b)))
      .map((value) => ({ value, label: value })),
    [data]
  );

  const filteredData = data.filter((reservation) => {
    const mappedStatus = statusMap[reservation.reservation_type] || reservation.reservation_type;
    const matchesStatus = !statusFilter || mappedStatus === statusFilter.value;
    const computedSourceName = getSourceName(reservation);
    const matchesPartner = !partnerFilter || computedSourceName === partnerFilter.value;
    const matchesPurpose = !purposeFilter || getPurpose(reservation) === purposeFilter.value;
    const petValue = getPetValue(reservation);
    const matchesPet = !petFilter || petValue === petFilter.value;
    const previousStays = getPreviousStays(reservation);
    const explicitRepeat = reservation.is_repeat_guest === true || reservation.is_repeat_guest === "true";
    const isRepeat = explicitRepeat || previousStays > 0;
    const matchesGuestType =
      !guestTypeFilter ||
      (guestTypeFilter.value === "repeat" ? isRepeat : !isRepeat);
    return matchesStatus && matchesPartner && matchesPurpose && matchesPet && matchesGuestType;
  });

  // Stats Calculation
  const totalConfirmed = filteredData.filter((r) => (statusMap[r.reservation_type] || r.reservation_type) === "confirmed").length;
  const totalCancelled = filteredData.filter((r) => (statusMap[r.reservation_type] || r.reservation_type) === "cancelled").length;
  const totalTentative = filteredData.filter((r) => (statusMap[r.reservation_type] || r.reservation_type) === "tentative").length;
  const cancellationRate = filteredData.length > 0 ? ((totalCancelled / filteredData.length) * 100).toFixed(1) : 0;

  const activityStats = [
    { label: "Confirmed", value: `${totalConfirmed}`, color: "#28a745" },
    { label: "Cancelled", value: `${totalCancelled}`, color: "#dc3545" },
    { label: "Tentative", value: `${totalTentative}`, color: "#007bff" },
    { label: "Cancellation Rate (%)", value: `${cancellationRate}%`, color: "#17a2b8" },
  ];

  // Memoized Chart Data
  const chartData = useMemo(() => {
    const dates = [...new Set(filteredData.map((r) => moment(r.check_in_date).format("DD MMM YYYY")))].sort();
    const sources = [...new Set(filteredData.map((r) => getSourceName(r)))];

    const reservationTrends = {
      labels: dates,
      datasets: [
        {
          label: "Confirmed",
          data: dates.map((date) => filteredData.filter((r) => (statusMap[r.reservation_type] || r.reservation_type) === "confirmed" && moment(r.check_in_date).format("DD MMM YYYY") === date).length),
          borderColor: "#28a745",
          backgroundColor: "rgba(40, 167, 69, 0.2)",
          fill: false,
        },
        {
          label: "Cancelled",
          data: dates.map((date) => filteredData.filter((r) => (statusMap[r.reservation_type] || r.reservation_type) === "cancelled" && moment(r.check_in_date).format("DD MMM YYYY") === date).length),
          borderColor: "#dc3545",
          backgroundColor: "rgba(220, 53, 69, 0.2)",
          fill: false,
        },
        {
          label: "Tentative",
          data: dates.map((date) => filteredData.filter((r) => (statusMap[r.reservation_type] || r.reservation_type) === "tentative" && moment(r.check_in_date).format("DD MMM YYYY") === date).length),
          borderColor: "#007bff",
          backgroundColor: "rgba(0, 123, 255, 0.2)",
          fill: false,
        },
      ],
    };

    const sourceDistribution = {
      labels: sources,
      datasets: [
        {
          label: "Reservations",
          data: sources.map((source) => filteredData.filter((r) => (getSourceName(r)) === source).length),
          backgroundColor: ["#007bff", "#28a745", "#dc3545", "#ffc107", "#6f42c1"],
        },
      ],
    };

    return { reservationTrends, sourceDistribution };
  }, [filteredData]);

  const reservationTrendsOptions = {
    responsive: true,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Reservation Count" } },
    },
  };

  const sourceDistributionOptions = {
    responsive: true,
    scales: {
      y: { beginAtZero: true, title: { display: true, text: "Number of Reservations" } },
    },
  };

  const handleDateRangeChange = (param) => {
    setDateRange((prevRange) => {
      let newRange;
      const business_date = localStorage.getItem("business_date");
      const referenceDate = business_date ? moment(business_date) : moment();

      if (typeof param === "string") {
        if (param === "today") {
          newRange = { startDate: referenceDate.startOf("day"), endDate: referenceDate.endOf("day") };
        } else if (param === "yesterday") {
          newRange = { startDate: referenceDate.subtract(1, "days").startOf("day"), endDate: referenceDate.subtract(1, "days").endOf("day") };
        } else if (param === "last7Days") {
          newRange = { startDate: referenceDate.subtract(7, "days").startOf("day"), endDate: referenceDate.endOf("day") };
        } else if (param === "last30Days") {
          newRange = { startDate: referenceDate.subtract(30, "days").startOf("day"), endDate: referenceDate.endOf("day") };
        } else if (param === "thisMonth") {
          newRange = { 
            startDate: referenceDate.startOf("month").startOf("day"), 
            endDate: referenceDate.endOf("month").endOf("day") 
          };
        } else if (param === "lastMonth") {
          newRange = { 
            startDate: referenceDate.subtract(1, "month").startOf("month").startOf("day"), 
            endDate: referenceDate.subtract(1, "month").endOf("month").endOf("day") 
          };
        } else if (param === "customRange") {
          return prevRange;
        }
      } else {
        const { start, end } = param;
        newRange = {
          startDate: moment(start).startOf("day"),
          endDate: moment(end).startOf("day"),
        };
      }

      if (!newRange?.startDate || !newRange?.endDate || !newRange.startDate.isValid() || !newRange.endDate.isValid()) {
        Swal.fire({
          title: t("error"),
          text: t("invalid_date_selected"),
          icon: "error",
        });
        setData([]);
        return prevRange;
      }

      if (newRange.startDate.isAfter(newRange.endDate)) {
        Swal.fire({
          title: t("error"),
          text: t("start_date_after_end_date"),
          icon: "error",
        });
        setData([]);
        return prevRange;
      }

      return newRange;
    });
  };

  const handleStatusFilterChange = (option) => {
    setStatusFilter(option);
  };

  const handlePartnerFilterChange = (value) => {
    setPartnerFilter(value);
  };

  const exportHeaders = [
    { key: "created_at", title: t("date") },
    { key: "booking_number", title: t("booking_number") },
    { key: "business_source_name", title: t("booking_source") },
    ...(capabilities.purpose ? [{ key: "visit_purpose", title: "Purpose of Stay" }] : []),
    ...(capabilities.repeat ? [{ key: "guest_type", title: "Guest Type" }] : []),
    ...(capabilities.pet ? [{ key: "travelling_with_pet", title: "Pet" }] : []),
    { key: "room_type", title: t("room_type") },
    { key: "reservation_type", title: t("status") },
    { key: "guest_name", title: t("guest_name") },
    { key: "check_in_date", title: t("check_in") },
    { key: "check_out_date", title: t("check_out") },
  ];

  const exportData = filteredData.map((reservation) => ({
    created_at: renderDate(reservation.created_at),
    booking_number: reservation.booking_number,
    business_source_name: getSourceName(reservation),
    ...(capabilities.purpose ? { visit_purpose: getPurpose(reservation) || "-" } : {}),
    ...(capabilities.repeat ? {
      guest_type:
        reservation.is_repeat_guest === true ||
        reservation.is_repeat_guest === "true" ||
        getPreviousStays(reservation) > 0
          ? "Repeat Guest"
          : "First Stay",
    } : {}),
    ...(capabilities.pet ? {
      travelling_with_pet:
        getPetValue(reservation) === true
          ? "Yes"
          : getPetValue(reservation) === false
          ? "No"
          : "-",
    } : {}),
    room_type: reservation.room_type || "-",
    reservation_type: (statusMap[reservation.reservation_type] || reservation.reservation_type).replace("_", " ").toUpperCase(),
    guest_name: reservation.guest_name || "-",
    check_in_date: renderDate(reservation.check_in_date),
    check_out_date: renderDate(reservation.check_out_date),
  }));

  const totalReservations = filteredData.length;
  const totalNights = filteredData.reduce(
    (sum, reservation) => sum + moment(reservation.check_out_date).diff(moment(reservation.check_in_date), "days"),
    0
  );
  const totalDirectBookings = filteredData.filter((reservation) =>
    isDirectSource(getSourceName(reservation))
  ).length;
  const totalPartnerBookings = Math.max(totalReservations - totalDirectBookings, 0);
  const directShare = totalReservations > 0
    ? ((totalDirectBookings / totalReservations) * 100).toFixed(1)
    : "0.0";

  const totalsForPageFooter = [
    { key: "Total Reservations", value: totalReservations },
    { key: "Total Nights", value: totalNights },
    { key: "Direct Bookings", value: totalDirectBookings },
    { key: "Partner / OTA Bookings", value: totalPartnerBookings },
    { key: "Direct Share", value: `${directShare}%` },
  ];

  const exportFooter = [
    { key: "Total Reservations", value: totalReservations },
    { key: "Total Nights", value: totalNights },
    { key: "Direct Bookings", value: totalDirectBookings },
    { key: "Partner / OTA Bookings", value: totalPartnerBookings },
    { key: "Direct Share", value: `${directShare}%` },
  ];

  const suffix = `_${moment().format("YYYYMMDD")}`;

  const handleExportXLS = () => {
    exportService.exportToXLS(
      exportData,
      exportHeaders,
      `reservation_report${suffix}.xlsx`,
      "Reservation Report"
    );
  };

  const handleExportCSV = () => {
    exportService.exportToCSV(
      exportData,
      exportHeaders,
      `reservation_report${suffix}.csv`
    );
  };

  const handleExportPDF = () => {
    exportService.exportToPDF(
      exportData,
      exportHeaders,
      "Reservation Report",
      `reservation_report${suffix}.pdf`,
      false,
      true,
      totalsForPageFooter,
      exportFooter
    );
  };

  const handlePrint = async () => {
    try {
      setLoading(true);
      const pdfBlob = await exportService.exportToPDF(
        exportData,
        exportHeaders,
        "Reservation Report",
        null,
        true,
        true,
        totalsForPageFooter,
        exportFooter
      );
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfData(pdfUrl);
      setPdfVisible(true);
    } catch (error) {
      console.error("Error generating PDF for preview:", error);
      message.error("Failed to load PDF preview.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setPdfVisible(false);
    setPdfData(null);
  };

  const downloadMenu = (
    <Menu>
      <Menu.Item key="csv" onClick={handleExportCSV}>
        Download CSV
      </Menu.Item>
      <Menu.Item key="xls" onClick={handleExportXLS}>
        Download XLS
      </Menu.Item>

      <Menu.Item key="print" onClick={handlePrint}>
        Print Preview
      </Menu.Item>
    </Menu>
  );

  const statusOptions = [
    { value: "confirmed", label: "Confirmed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "tentative", label: "Tentative" },
    { value: "checked_out", label: "Checked Out" },
    { value: "no_show", label: "No Show" },
  ];

  const bookingData = filteredData
    .filter((reservation) => reservation.booking_number)
    .map((reservation, index) => ({
      key: `booking-${index}`,
      created_at: reservation.created_at,
      check_in: reservation.check_in_date,
      check_out: reservation.check_out_date,
      booking_number: reservation.booking_number,
      business_source_name: getSourceName(reservation),
      visit_purpose: getPurpose(reservation) || "-",
      guest_type:
        reservation.is_repeat_guest === true ||
        reservation.is_repeat_guest === "true" ||
        getPreviousStays(reservation) > 0
          ? "Repeat Guest"
          : "First Stay",
      travelling_with_pet:
        getPetValue(reservation) === true
          ? "Yes"
          : getPetValue(reservation) === false
          ? "No"
          : "-",
      room_type: reservation.room_type || "-",
      reservation_type: reservation.reservation_type,
      guest_name: reservation.guest_name || "-",
    }));

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="page-header page-top-icons">
          <div className="row align-items-center">
            <div className="col-md-12">
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3">
                  <h3 className="page-title" style={{ minWidth: "300px", whiteSpace: "nowrap" }}>
                    Reservation Report
                  </h3>
                  <DatePicker
                    initialSettings={{
                      singleDatePicker: false,
                      showDropdowns: true,
                      startDate: dateRange.startDate.toDate(),
                      endDate: dateRange.endDate.toDate(),
                      ranges: {
                        Today: [
                          moment(localStorage.getItem("business_date")).startOf("day").toDate(),
                          moment(localStorage.getItem("business_date")).endOf("day").toDate()
                        ],
                        Yesterday: [
                          moment(localStorage.getItem("business_date")).subtract(1, "days").startOf("day").toDate(),
                          moment(localStorage.getItem("business_date")).subtract(1, "days").endOf("day").toDate()
                        ],
                        "Last 7 Days": [
                          moment(localStorage.getItem("business_date")).subtract(7, "days").startOf("day").toDate(),
                          moment(localStorage.getItem("business_date")).endOf("day").toDate()
                        ],
                        "Last 30 Days": [
                          moment(localStorage.getItem("business_date")).subtract(30, "days").startOf("day").toDate(),
                          moment(localStorage.getItem("business_date")).endOf("day").toDate()
                        ],
                        "This Month": [
                          moment(localStorage.getItem("business_date")).startOf("month").startOf("day").toDate(),
                          moment(localStorage.getItem("business_date")).endOf("month").endOf("day").toDate()
                        ],
                        "Last Month": [
                          moment(localStorage.getItem("business_date")).subtract(1, "month").startOf("month").startOf("day").toDate(),
                          moment(localStorage.getItem("business_date")).subtract(1, "month").endOf("month").endOf("day").toDate()
                        ],
                      },
                      locale: {
                        format: "DD MMM YYYY",
                        applyLabel: "Select",
                        cancelLabel: "Cancel",
                      },
                    }}
                    onApply={(event, picker) => {
                      handleDateRangeChange({ start: picker.startDate, end: picker.endDate });
                    }}
                  >
                    <div className="input-group" style={{ width: "250px" }}>
                      <input
                        type="text"
                        className="form-control text-center fw-bold"
                        value={`${dateRange.startDate.format("DD MMM YYYY")} - ${dateRange.endDate.format("DD MMM YYYY")}`}
                        style={{ height: "38px", fontSize: "15px", borderRight: "none", backgroundColor: "#ffffff" }}
                        readOnly
                      />
                      <div className="input-group-append">
                        <span
                          className="input-group-text"
                          style={{
                            backgroundColor: "#fff",
                            borderLeft: "none",
                            borderRadius: "0 4px 4px 0",
                            cursor: "pointer",
                            height: "38px",
                          }}
                        >
                          <i className="fa fa-chevron-down" style={{ color: "#495057" }} />
                        </span>
                      </div>
                    </div>
                  </DatePicker>
                </div>
                <div className="d-flex align-items-center flex-shrink-0 gap-2">
                  <div className="view-icons d-flex align-items-center gap-1">
                    <Link to="#" className="btn btn-link" data-bs-toggle="modal" data-bs-target="#scrollable-modal">
                      <i className="las la-filter" />
                    </Link>
                    <Dropdown overlay={downloadMenu} trigger={["click"]} placement="bottomRight">
                      <Button className="custom-btn-drop d-inline-flex align-items-center" type="text">
                        <i className="la la-download custom-icon me-2" /> Download <DownOutlined className="ms-2" />
                      </Button>
                    </Dropdown>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <PrintReportPreview
            visible={pdfVisible}
            onClose={handleCloseModal}
            reportTitle="Reservation Report"
            propertyName="Your Property Name"
            data={exportData}
            headers={exportHeaders}
            footerData={totalsForPageFooter}
            totalColumns={["Total Reservations", "Total Nights"]}
            onDownloadPDF={handleExportPDF}
            apiEndpoint="transactions/generate-pdf"
            apiUrl={process.env.REACT_APP_API_URL}
          />
        </div>
        <div
          style={{
            margin: "0 20px 16px",
            padding: "16px",
            border: "1px solid #e4e7ec",
            borderRadius: 14,
            background: "linear-gradient(135deg, #f8fbff 0%, #ffffff 72%)",
            boxShadow: "0 8px 24px rgba(16, 24, 40, 0.05)",
          }}
        >
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#101828" }}>Business Impact</div>
              <div style={{ fontSize: 11, color: "#667085", marginTop: 2 }}>
                Source mix and stay volume for the selected period.
              </div>
            </div>
            <div style={{ fontSize: 10, color: "#667085", fontWeight: 700 }}>
              {dateRange.startDate.format("DD MMM")} – {dateRange.endDate.format("DD MMM YYYY")}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
            {[
              ["Reservations", totalReservations],
              ["Nights", totalNights],
              ["Direct", totalDirectBookings],
              ["Partner / OTA", totalPartnerBookings],
              ["Direct Share", `${directShare}%`],
            ].map(([label, value]) => (
              <div key={label} style={{ border: "1px solid #eef2f6", borderRadius: 10, padding: "12px 13px", background: "#fff" }}>
                <div style={{ fontSize: 10, color: "#667085", fontWeight: 800, textTransform: "uppercase", letterSpacing: ".05em" }}>{label}</div>
                <div style={{ marginTop: 5, fontSize: 20, color: "#101828", fontWeight: 800 }}>{value}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="row g-4 mb-4" style={{ padding: "15px 20px" }}>
          {activityStats.map((stat, index) => (
            <div
              key={index}
              className="col-md-2 col-6 stats-box"
              style={{ margin: "0 15px", borderRadius: "8px", boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)", padding: "10px", position: "relative" }}
            >
              <div
                className="stats-line"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: "4px",
                  backgroundColor: stat.color,
                  borderRadius: "4px 0 0 4px",
                }}
              ></div>
              <div className="stats-content" style={{ paddingLeft: "10px" }}>
                <h2 style={{ fontSize: "1.5rem", margin: 0, fontFamily: "'Roboto', sans-serif" }}>{stat.value}</h2>
                <p style={{ margin: 0, color: "#666", fontFamily: "'Roboto', sans-serif" }}>{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {showGraph && (
          <div className="row mb-4">
            <div className="col-md-6">
              <div
                className="card"
                style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}
              >
                <div
                  className="card-header"
                  style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}
                  >
                    {t("reservation_trends")}
                  </h5>
                  <div
                    style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#28a745" }}
                  />
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <Line data={chartData.reservationTrends} options={reservationTrendsOptions} />
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div
                className="card"
                style={{ borderRadius: "0", overflowX: "auto", border: "1px solid #dee2e6", borderBottom: "none" }}
              >
                <div
                  className="card-header"
                  style={{ backgroundColor: "#fff", padding: "12px 16px", borderBottom: "none", position: "relative" }}
                >
                  <h5
                    className="mb-0 d-flex align-items-center"
                    style={{ fontSize: "14px", fontWeight: 600, color: "#333", textTransform: "uppercase", letterSpacing: "0.5px" }}
                  >
                    {t("booking_source_distribution")}
                  </h5>
                  <div
                    style={{ position: "absolute", bottom: "0", left: "0", width: "100%", height: "4px", backgroundColor: "#007bff" }}
                  />
                </div>
                <div className="card-body p-0">
                  {loading ? (
                    <div className="text-center my-5">
                      <div className="loader">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                      </div>
                    </div>
                  ) : (
                    <Bar data={chartData.sourceDistribution} options={sourceDistributionOptions} />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="table-responsive" style={{ minHeight: "60vh", height: "calc(100vh - 150px)", overflow: "auto" }}>
          {loading ? (
            <div
              id="loader-wrapper-"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <div className="loader">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
            </div>
          ) : bookingData.length === 0 ? (
            <div
              className="no-data-container text-center mt-4"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src="https://ui-cdn.digitalocean.com/apps/ui-spaces/static/media/spaces-empty.5e1d7947c43b7e336b14e421d4a86639.svg"
                alt="No data available"
                style={{ width: "150px", marginBottom: "20px" }}
              />
              <h3>No Data Available</h3>
              <p>No reservations found for the selected date range.</p>
            </div>
          ) : (
            <Table
              columns={[
                {
                  title: t("date"),
                  dataIndex: "created_at",
                  key: "created_at",
                  render: (text) => (text ? renderDate(text) : "-"),
                  sorter: (a, b) => (a.created_at ? moment(a.created_at).unix() : 0) - (b.created_at ? moment(b.created_at).unix() : 0),
                  width: 150,
                },
                {
                  title: t("booking_number"),
                  dataIndex: "booking_number",
                  key: "booking_number",
                  render: (text) => text || "-",
                  sorter: (a, b) => (a.booking_number || "").localeCompare(b.booking_number || ""),
                  width: 200,
                },
                {
                  title: t("booking_source"),
                  dataIndex: "business_source_name",
                  key: "business_source_name",
                  render: (text) => text || "-",
                  sorter: (a, b) => (a.business_source_name || "").localeCompare(b.business_source_name || ""),
                  width: 150,
                },
                ...(capabilities.purpose ? [{
                  title: "Purpose of Stay",
                  dataIndex: "visit_purpose",
                  key: "visit_purpose",
                  render: (text) => text || "-",
                  sorter: (a, b) => (a.visit_purpose || "").localeCompare(b.visit_purpose || ""),
                  width: 170,
                }] : []),
                ...(capabilities.repeat ? [{
                  title: "Guest Type",
                  dataIndex: "guest_type",
                  key: "guest_type",
                  render: (text) => (
                    <span style={{ fontWeight: 700, color: text === "Repeat Guest" ? "#027a48" : "#175cd3" }}>
                      {text || "-"}
                    </span>
                  ),
                  width: 140,
                }] : []),
                ...(capabilities.pet ? [{
                  title: "Pet",
                  dataIndex: "travelling_with_pet",
                  key: "travelling_with_pet",
                  render: (text) => text || "-",
                  width: 90,
                }] : []),
                {
                  title: t("room_type"),
                  dataIndex: "room_type",
                  key: "room_type",
                  render: (text) => text || "-",
                  sorter: (a, b) => (a.room_type || "").localeCompare(b.room_type || ""),
                  width: 150,
                },
                {
                  title: t("status"),
                  dataIndex: "reservation_type",
                  key: "reservation_type",
                  render: (status) => (
                    status ? (
                      <span
                        className={`colored-box status-box ${
                          (statusMap[status] || status) === "confirmed"
                            ? "bg-success"
                            : (statusMap[status] || status) === "cancelled"
                            ? "bg-danger"
                            : (statusMap[status] || status) === "tentative"
                            ? "bg-primary"
                            : (statusMap[status] || status) === "checked_out"
                            ? "bg-secondary"
                            : (statusMap[status] || status) === "no_show"
                            ? "bg-warning"
                            : "bg-default"
                        }`}
                      >
                        {(statusMap[status] || status).replace("_", " ").toUpperCase()}
                      </span>
                    ) : "-"
                  ),
                  width: 150,
                },
                {
                  title: t("guest_name"),
                  dataIndex: "guest_name",
                  key: "guest_name",
                  render: (text) => text || "-",
                  sorter: (a, b) => (a.guest_name || "").localeCompare(b.guest_name || ""),
                  width: 200,
                },
                {
                  title: t("check_in"),
                  dataIndex: "check_in",
                  key: "check_in",
                  render: (text) => (text ? renderDate(text) : "-"),
                  sorter: (a, b) => (a.check_in ? moment(a.check_in).unix() : 0) - (b.check_in ? moment(b.check_in).unix() : 0),
                  width: 150,
                },
                {
                  title: t("check_out"),
                  dataIndex: "check_out",
                  key: "check_out",
                  render: (text) => (text ? renderDate(text) : "-"),
                  sorter: (a, b) => (a.check_out ? moment(a.check_out).unix() : 0) - (b.check_out ? moment(b.check_out).unix() : 0),
                  width: 150,
                },
              ]}
              dataSource={bookingData}
              pagination={{ pageSize: 20 }}
              className="table-striped custom-table"
              rowKey={(record) => record.key}
              scroll={{ x: 2000, y: "calc(100vh - 150px)" }}
            />
          )}
        </div>

        <div
          className="modal custom-modal fade"
          id="scrollable-modal"
          tabIndex={-1}
          role="dialog"
          aria-labelledby="filterModalTitle"
          aria-hidden="true"
        >
          <div
            className="modal-dialog modal-lg"
            style={{
              margin: 0,
              position: "fixed",
              right: 0,
              height: "100vh",
              width: "40%",
              zIndex: 1060,
            }}
            role="document"
          >
            <div className="modal-content h-100">
              <div className="modal-header">
                <h5 className="modal-title" id="filterModalTitle">
                  Filter
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div
                className="modal-body"
                style={{ maxHeight: "calc(100vh - 80px)", overflowY: "auto" }}
              >
                <div className="row">
                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Status:</label>
                      <Select
                        options={statusOptions}
                        placeholder="Select Status"
                        value={statusFilter}
                        onChange={handleStatusFilterChange}
                        isClearable
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: "300px",
                            borderRadius: 4,
                            borderColor: "#d9d9d9",
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 1070,
                          }),
                        }}
                        menuPortalTarget={document.body}
                      />
                    </div>
                  </div>

                  <div className="col-sm-12 col-md-12 mb-3">
                    <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                      <label className="form-label me-2" style={{ minWidth: "150px" }}>Booking Source:</label>
                      <SelectPartner
                        selectedValue={partnerFilter}
                        onSelectPartner={handlePartnerFilterChange}
                        isDisabled={false}
                      />
                    </div>
                  </div>

                  {capabilities.purpose && (
                    <div className="col-sm-12 col-md-12 mb-3">
                      <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                        <label className="form-label me-2" style={{ minWidth: "150px" }}>Purpose of Stay:</label>
                        <Select
                          options={purposeOptions}
                          placeholder="Select Purpose"
                          value={purposeFilter}
                          onChange={setPurposeFilter}
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1070 }), control: (base) => ({ ...base, minWidth: "300px" }) }}
                        />
                      </div>
                    </div>
                  )}

                  {capabilities.repeat && (
                    <div className="col-sm-12 col-md-12 mb-3">
                      <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                        <label className="form-label me-2" style={{ minWidth: "150px" }}>Guest Type:</label>
                        <Select
                          options={[{ value: "repeat", label: "Repeat Guest" }, { value: "first", label: "First Stay" }]}
                          placeholder="Select Guest Type"
                          value={guestTypeFilter}
                          onChange={setGuestTypeFilter}
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1070 }), control: (base) => ({ ...base, minWidth: "300px" }) }}
                        />
                      </div>
                    </div>
                  )}

                  {capabilities.pet && (
                    <div className="col-sm-12 col-md-12 mb-3">
                      <div className="form-group d-flex align-items-center" style={{ borderBottom: "1px solid #dcdcdc", paddingBottom: "10px" }}>
                        <label className="form-label me-2" style={{ minWidth: "150px" }}>Travelling with Pet:</label>
                        <Select
                          options={[{ value: true, label: "Yes" }, { value: false, label: "No" }]}
                          placeholder="Select Pet Status"
                          value={petFilter}
                          onChange={setPetFilter}
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 1070 }), control: (base) => ({ ...base, minWidth: "300px" }) }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                >
                  Close
                </button>
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => {
                    setStatusFilter(null);
                    setPartnerFilter(null);
                    setPurposeFilter(null);
                    setPetFilter(null);
                    setGuestTypeFilter(null);
                  }}
                  data-bs-dismiss="modal"
                >
                  Reset
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReservationReport;